import { type FC, useMemo } from "react";
import { Dialog, LinearProgress, Box, CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  usePlatformPageQuery,
  useUpdatePlatformPageMutation,
} from "@/partner/entities/platformPages";
import { ErrorMessage } from "@/shared/components";
import {
  mapPlatformPageToFormValues,
  type PlatformPageFormValues,
} from "../model";
import { PageForm } from "./PageForm";

interface EditPageDialogProps {
  open: boolean;
  pageId: string | null;
  onClose: () => void;
  onCompleted?: () => void;
}

export const EditPageDialog: FC<EditPageDialogProps> = ({
  open,
  pageId,
  onClose,
  onCompleted,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { data, loading, error } = usePlatformPageQuery({
    variables: { id: String(pageId) },
    skip: !pageId || !open,
    fetchPolicy: "network-only",
  });

  const [updatePlatformPage, { loading: updating }] =
    useUpdatePlatformPageMutation({
      onCompleted: () => {
        enqueueSnackbar(t("platformPages.snackbar.updated"), {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["PlatformPagesList"],
    });

  const initialValues = useMemo(
    () => (data ? mapPlatformPageToFormValues(data.platformPage) : undefined),
    [data]
  );

  const handleSubmit = async (values: PlatformPageFormValues) => {
    await updatePlatformPage({
      variables: {
        id: pageId,
        input: values,
      },
    });

    onCompleted?.();
  };

  if (loading) {
    return (
      <Dialog
        fullScreen
        fullWidth
        maxWidth="md"
        scroll="body"
        open={open}
        onClose={onClose}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog
        fullScreen
        fullWidth
        maxWidth="md"
        scroll="body"
        open={open}
        onClose={onClose}
      >
        <Box sx={{ p: 2 }}>
          <ErrorMessage error={error} />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      fullScreen
      fullWidth
      maxWidth="md"
      scroll="body"
      open={open}
      onClose={onClose}
    >
      {updating && <LinearProgress />}
      <PageForm
        title={t("platformPages.edit.title")}
        loading={updating}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onClose={onClose}
      />
    </Dialog>
  );
};
